import type { TopicDraft } from "../domain/draft";
import type { DraftRevision, RevisionImage } from "../domain/revisions";
import type { AgentTask, ExecutorProfile, SyncRequest, SyncResponse, Topic } from "../types";

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export async function postSync(body: SyncRequest, token: string): Promise<SyncResponse> {
  const response = await fetch("/api/sync", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) throw new ApiError(response.status, payload.error || "同步失败。");
  return payload as SyncResponse;
}

export async function fetchDraft(topicId: string, token: string): Promise<TopicDraft> {
  const response = await fetch(`/api/topics/${encodeURIComponent(topicId)}/draft`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) throw new ApiError(response.status, payload.error || "稿子没有读出来。");
  return payload as TopicDraft;
}

export async function postReturn(topicId: string, comment: string, token: string): Promise<Topic> {
  const response = await fetch(`/api/topics/${encodeURIComponent(topicId)}/return`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ comment }),
  });
  const payload = (await response.json().catch(() => ({}))) as { error?: string; topic?: Topic };
  if (!response.ok || !payload.topic) throw new ApiError(response.status, payload.error || "退回没有写进 Notion。");
  return payload.topic;
}

export async function postApprove(
  topicId: string,
  body: { title: string; body: string; tags: string[] },
  token: string,
): Promise<Topic> {
  const response = await fetch(`/api/topics/${encodeURIComponent(topicId)}/approve`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as { error?: string; topic?: Topic };
  if (!response.ok || !payload.topic) throw new ApiError(response.status, payload.error || "通过没有写进 Notion。");
  return payload.topic;
}

export interface RewriteBody {
  title: string;
  body: string;
  tags: string[];
  images: RevisionImage[];
  evaluation: string;
  suggestion: string;
  targets: string[];
  styleVersionId: string | null;
}

export interface RewriteResponse {
  topic: Topic;
  task: AgentTask;
  revision: DraftRevision;
  waiting: boolean;
  stored: boolean;
}

export async function fetchRevisions(topicId: string, token: string): Promise<{ revisions: DraftRevision[]; stored: boolean }> {
  const response = await fetch(`/api/topics/${encodeURIComponent(topicId)}/revisions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = (await response.json().catch(() => ({}))) as { error?: string; revisions?: DraftRevision[]; stored?: boolean };
  if (!response.ok) throw new ApiError(response.status, payload.error || "审阅记录没有读出来。");
  return { revisions: payload.revisions ?? [], stored: payload.stored !== false };
}

export async function postRewrite(topicId: string, body: RewriteBody, token: string): Promise<RewriteResponse> {
  const response = await fetch(`/api/topics/${encodeURIComponent(topicId)}/rewrite`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as Partial<RewriteResponse> & { error?: string };
  if (!response.ok || !payload.topic || !payload.task || !payload.revision) {
    throw new ApiError(response.status, payload.error || "按建议重新生成没有提交。");
  }
  return {
    topic: payload.topic,
    task: payload.task,
    revision: payload.revision,
    waiting: Boolean(payload.waiting),
    stored: Boolean(payload.stored),
  };
}

export interface ExecutorWrite {
  name: string;
  wakeUrl: string;
  headerName: string;
  headerValue: string;
  taskTypes: string[];
  enabled?: boolean;
}

export interface WakeHit {
  executorId: string;
  name: string;
  status: number | null;
  error: string;
}

async function apiJson<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) throw new ApiError(response.status, payload.error || "请求没有完成。");
  return payload as T;
}

export function createExecutor(body: ExecutorWrite, token: string): Promise<{ executor: ExecutorProfile; apiKey: string }> {
  return apiJson("/api/executors", token, { method: "POST", body: JSON.stringify(body) });
}

export function updateExecutor(id: string, body: ExecutorWrite, token: string): Promise<{ executor: ExecutorProfile }> {
  return apiJson(`/api/executors/${encodeURIComponent(id)}`, token, { method: "PATCH", body: JSON.stringify(body) });
}

export function rotateExecutorKey(id: string, token: string): Promise<{ executor: ExecutorProfile; apiKey: string }> {
  return apiJson(`/api/executors/${encodeURIComponent(id)}/key`, token, { method: "POST" });
}

export function testExecutorWake(id: string, token: string): Promise<{ executor: ExecutorProfile; result: WakeHit }> {
  return apiJson(`/api/executors/${encodeURIComponent(id)}/wake`, token, { method: "POST" });
}

export function rewakeQueuedTask(id: string, token: string): Promise<{ results: WakeHit[]; error?: string }> {
  return apiJson(`/api/tasks/${encodeURIComponent(id)}/wake`, token, { method: "POST" });
}
